/**
 * Frontier Timeline — single chronological feed merging all dated catalogues.
 *
 * What just happened in frontier AI, across every category we track,
 * sorted newest-first. Click any row to jump to its source page.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Activity, RefreshCw, Filter } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface TimelineItem {
  date: string
  category: string
  route: string
  title: string
  summary: string
  _id: string
}

const CATEGORY_TONE: Record<string, string> = {
  'incident':     'bg-rose/15 text-rose-light',
  'release':      'bg-purple/15 text-purple-light',
  'regulation':   'bg-blue/15 text-blue-light',
  'lawsuit':      'bg-rose/15 text-rose-light',
  'departure':    'bg-amber/15 text-amber-light',
  'open-letter':  'bg-blue/15 text-blue-light',
  'funding':      'bg-emerald/15 text-emerald',
  'compute-build':'bg-purple/15 text-purple-light',
  'acquisition':  'bg-emerald/15 text-emerald',
  'conference':   'bg-cyan/15 text-cyan',
  'energy':       'bg-amber/15 text-amber-light',
  'ban':          'bg-rose/15 text-rose-light',
  'testimony':    'bg-blue/15 text-blue-light',
  'aisi-eval':    'bg-emerald/15 text-emerald',
  'graveyard':    'bg-rose/15 text-rose-light',
  'agent-launch': 'bg-amber/15 text-amber-light',
  'data-deal':    'bg-blue/15 text-blue-light',
  'capability':   'bg-purple/15 text-purple-light',
  'scientific':   'bg-emerald/15 text-emerald',
}

export default function FrontierTimelinePage() {
  const [items, setItems] = useState<TimelineItem[]>([])
  const [allCats, setAllCats] = useState<string[]>([])
  const [catFilter, setCatFilter] = useState('')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [generatedAt, setGeneratedAt] = useState<string | null>(null)

  const refresh = () => {
    setLoading(true)
    api.get('/api/frontier/timeline', { params: { limit: 300 } })
      .then(r => {
        setItems(r.data?.items || [])
        setAllCats(r.data?.categories || [])
        setGeneratedAt(r.data?.generated_at || null)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { refresh() }, [])

  const filtered = useMemo(() => items.filter(i =>
    (!catFilter || i.category === catFilter) &&
    (!search || `${i.title} ${i.summary}`.toLowerCase().includes(search.toLowerCase()))
  ), [items, catFilter, search])

  // Group by year-month for visual chunking
  const grouped = useMemo(() => {
    const out: Record<string, TimelineItem[]> = {}
    for (const item of filtered) {
      const ym = item.date.slice(0, 7)
      if (!out[ym]) out[ym] = []
      out[ym].push(item)
    }
    return Object.entries(out).sort(([a], [b]) => b.localeCompare(a))
  }, [filtered])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Activity size={18} className="text-amber-light" /> FRONTIER TIMELINE
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          unified chronological feed · all catalogues
        </span>
        {generatedAt && (
          <span className="text-[10px] text-text-muted font-mono">
            {generatedAt.slice(0, 16).replace('T', ' ')} UTC
          </span>
        )}
        <button type="button" onClick={refresh} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="flex items-center gap-2 text-xs flex-wrap">
        <Filter size={12} className="text-text-muted" />
        <select className="input" value={catFilter} onChange={e => setCatFilter(e.target.value)}
          aria-label="Filter by category">
          <option value="">All categories ({items.length})</option>
          {allCats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input className="input flex-1 min-w-[200px]" placeholder="Search timeline"
          value={search} onChange={e => setSearch(e.target.value)} aria-label="Search timeline" />
        <span className="text-text-muted ml-auto">{filtered.length} of {items.length}</span>
      </div>

      {grouped.map(([ym, group]) => (
        <section key={ym}>
          <h2 className="text-[10px] uppercase tracking-[0.3em] text-text-muted mb-2 sticky top-0 bg-base py-1 z-10">
            {ym}
          </h2>
          <ol className="relative border-l border-border ml-3 space-y-2 pl-5">
            {group.map(item => (
              <li key={`${item._id}-${item.date}-${item.category}`} className="relative">
                <span className="absolute -left-[26px] top-2 w-2.5 h-2.5 rounded-full bg-amber border-2 border-base" />
                <Link to={item.route} className="card p-3 block hover:border-purple/40">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-[11px] font-mono text-amber-light shrink-0">{item.date}</span>
                    <span className={clsx('badge text-[9px] shrink-0', CATEGORY_TONE[item.category] || 'bg-surface-raised text-text-muted')}>
                      {item.category}
                    </span>
                    <span className="text-sm text-text-primary leading-snug flex-1">{item.title}</span>
                  </div>
                  {item.summary && (
                    <p className="text-xs text-text-secondary mt-1 leading-relaxed line-clamp-2">{item.summary}</p>
                  )}
                </Link>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  )
}
